import moment from 'moment-timezone'
import { useState } from 'react'
import { CheckOutlined, WarningOutlined } from '@ant-design/icons'
import { message } from 'antd'
import { useMutation } from '@apollo/client'
import { BORDER, TEXT_LABEL_STYLE } from '@User/MyAccountPage/styleConstants'
import pageStyles from '@User/MyAccountPage/styles.css'

import Box from '~/core/Box'
import Text from '~/core/Text'
import { IdentityType } from '~/redux-modules/identity/type'
import Link from '~/core/Link'
import logger from '~/lib/logger'
import getColor from '~/lib/getColor'
import { sendEmailVerification as SendEmailVerificationType } from '../__generated__/sendEmailVerification'
import { sendEmailVerificationMutation } from '../mutations'
import styles from '../styles.css'

type EmailAddressSectionPropsType = {
    identity: IdentityType
}

export default function EmailAddressSection(
    props: EmailAddressSectionPropsType,
) {
    const { identity } = props

    const [sendingEmailVerification, setSendingEmailVerification] =
        useState<boolean>(false)
    const [emailVerificationSent, setEmailVerificationSent] =
        useState<boolean>(false)
    const [sendEmailVerification] = useMutation<SendEmailVerificationType>(
        sendEmailVerificationMutation,
    )

    const sendEmail = async (evt: Event) => {
        evt.preventDefault()

        setSendingEmailVerification(true)
        const variables = { userId: identity.id }

        try {
            await sendEmailVerification({ variables })
            message.success('Email sent successfully!')
            setEmailVerificationSent(true)
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'My Account Page:: Failed to send verification email:  ',
                    variables,
                },
            })
            message.error(
                'We encountered an error while trying to send the email',
            )
        } finally {
            setSendingEmailVerification(false)
        }
    }

    let sendVerificationEmailText = 'Send verification email'

    if (emailVerificationSent) {
        sendVerificationEmailText = 'Verification email sent'
    } else if (sendingEmailVerification) {
        sendVerificationEmailText = 'Sending verification email'
    }

    return (
        <Box
            className={pageStyles.mobileColBox}
            padding="24px 0"
            borderBottom={BORDER}
        >
            <Box className={pageStyles.mobileLabel}>
                <Text style={TEXT_LABEL_STYLE}>EMAIL ADDRESS</Text>
            </Box>
            <Box position="relative" flex="1">
                <Box direction="col">
                    <Text className={styles.wrapText}>{identity.email}</Text>
                    <Text color="blackAlpha45">
                        {identity.emailVerified ? (
                            <>
                                <CheckOutlined
                                    style={{
                                        color: getColor('success'),
                                        marginRight: '5px',
                                    }}
                                />
                                Verified on{' '}
                                {moment(identity.emailVerifiedDate).format(
                                    'LL',
                                )}
                            </>
                        ) : (
                            <>
                                <WarningOutlined
                                    style={{
                                        color: getColor('warning'),
                                        marginRight: '5px',
                                    }}
                                />
                                Not verified
                            </>
                        )}
                    </Text>
                </Box>
                {!identity.emailVerified && (
                    <Box marginLeft="auto">
                        <Link
                            onClick={sendEmail}
                            placement="collapsable-box-with-linktext"
                            name="collapsable-box-with-linktext"
                            disabled={
                                sendingEmailVerification ||
                                emailVerificationSent
                            }
                        >
                            {sendVerificationEmailText}
                        </Link>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
